import type {
  InvestmentsListFamily,
  InvestmentsListRow,
  ValuationZone,
} from "@features/investments/api";
import type {
  DashboardETSort,
  DashboardSort,
  EquityFilters,
  ETFSortKey,
} from "@features/investments/state";

export type TriageSummary = {
  actionCounts: Record<ValuationZone, number>;
  topActionable: InvestmentsListRow[];
  attentionRows: InvestmentsListRow[];
  staleRows: InvestmentsListRow[];
  missingTickers: string[];
  configuredCount: number;
  cachedCount: number;
};

export type PortfolioFamilySummary = {
  family: InvestmentsListFamily;
  rows: InvestmentsListRow[];
  missingTickers: string[];
  cachedCount: number;
  configuredCount: number;
  medianOverall: number;
  medianValuation: number;
  actionCounts: Record<ValuationZone, number>;
  totalRedFlags: number;
  totalWarnings: number;
  strongest: InvestmentsListRow[];
  weakest: InvestmentsListRow[];
};

export type ETFSummary = {
  missingTickers: string[];
  configuredCount: number;
  cachedCount: number;
  assetClassMix: Array<{ label: string; count: number }>;
  medianExpenseRatio: number | null;
  highestAum: InvestmentsListRow[];
  lowestExpense: InvestmentsListRow[];
};

const actionRank: Record<ValuationZone, number> = {
  BUY: 0,
  HOLD: 1,
  WATCHLIST: 2,
  AVOID: 3,
  UNAVAILABLE: 4,
};

const moatBandRank: Record<string, number> = {
  HIGH: 0,
  MEDIUM: 1,
  FRAGILE: 2,
};

const confidenceRank: Record<string, number> = {
  HIGH: 0,
  MEDIUM: 1,
  LOW: 2,
};

const qualityTierRank: Record<string, number> = {
  PREMIUM: 0,
  STANDARD: 1,
  NEUTRAL: 2,
  CAUTION: 3,
};

const attentionConfidenceRank: Record<string, number> = {
  LOW: 0,
  MEDIUM: 1,
  HIGH: 2,
};

export function collectConfiguredTickers(families: InvestmentsListFamily[], rows: InvestmentsListRow[]): string[] {
  const tickers = new Set<string>();
  for (const family of families) {
    for (const ticker of family.tickers || []) {
      tickers.add(ticker);
    }
  }
  if (tickers.size === 0) {
    for (const row of rows) {
      tickers.add(row.ticker);
    }
  }
  return Array.from(tickers).sort();
}

export function buildFamilyRows(
  families: InvestmentsListFamily[],
  rows: InvestmentsListRow[],
): Array<{ family: InvestmentsListFamily; rows: InvestmentsListRow[]; missingTickers: string[] }> {
  const rowMap = new Map(rows.map((row) => [row.ticker, row]));
  return families.map((family) => {
    const familyRows: InvestmentsListRow[] = [];
    const missingTickers: string[] = [];
    for (const ticker of family.tickers || []) {
      const row = rowMap.get(ticker);
      if (row) {
        familyRows.push(row);
      } else {
        missingTickers.push(ticker);
      }
    }
    return {
      family,
      rows: familyRows,
      missingTickers,
    };
  });
}

export function applyEquityFilters(
  rows: InvestmentsListRow[],
  searchTerm: string,
  filters: EquityFilters,
): InvestmentsListRow[] {
  const needle = searchTerm.trim().toLowerCase();
  return rows.filter((row) => {
    if (needle !== "") {
      const haystack = `${row.ticker} ${row.companyName}`.toLowerCase();
      if (!haystack.includes(needle)) {
        return false;
      }
    }
    if (filters.familyKey && !(row.families || []).includes(filters.familyKey)) {
      return false;
    }
    if (filters.finalAction && row.finalAction !== filters.finalAction) {
      return false;
    }
    if (filters.valuationZone && row.valuationZone !== filters.valuationZone) {
      return false;
    }
    if (filters.moatBand && row.moatBand !== filters.moatBand) {
      return false;
    }
    if (filters.dataConfidence && row.dataConfidence !== filters.dataConfidence) {
      return false;
    }
    if (filters.buyLevelsQualityTier && row.buyLevelsQualityTier !== filters.buyLevelsQualityTier) {
      return false;
    }
    if (filters.hasWarnings === "yes" && (row.warningCount ?? 0) <= 0) {
      return false;
    }
    if (filters.hasWarnings === "no" && (row.warningCount ?? 0) > 0) {
      return false;
    }
    if (filters.hasRedFlags === "yes" && (row.redFlagCount ?? 0) <= 0) {
      return false;
    }
    if (filters.hasRedFlags === "no" && (row.redFlagCount ?? 0) > 0) {
      return false;
    }
    return true;
  });
}

export function applyETFSearchAndFilter(
  rows: InvestmentsListRow[],
  searchTerm: string,
  familyKey: string,
): InvestmentsListRow[] {
  const needle = searchTerm.trim().toLowerCase();
  return rows.filter((row) => {
    if (familyKey && !(row.families || []).includes(familyKey)) {
      return false;
    }
    if (needle === "") {
      return true;
    }
    const haystack = `${row.ticker} ${row.companyName}`.toLowerCase();
    return haystack.includes(needle);
  });
}

export function compareEquityRows(a: InvestmentsListRow, b: InvestmentsListRow, sort: DashboardSort): number {
  const modifier = sort.direction === "asc" ? 1 : -1;
  switch (sort.key) {
    case "ticker":
    case "companyName":
      return compareStrings(a[sort.key], b[sort.key]) * modifier;
    case "finalAction":
      return compareRanks(a.finalAction, b.finalAction, actionRank) * modifier;
    case "valuationZone":
      return compareRanks(a.valuationZone, b.valuationZone, actionRank) * modifier;
    case "moatBand":
      return compareRanks(a.moatBand, b.moatBand, moatBandRank) * modifier;
    case "dataConfidence":
      return compareRanks(a.dataConfidence, b.dataConfidence, confidenceRank) * modifier;
    case "buyLevelsQualityTier":
      return compareRanks(a.buyLevelsQualityTier, b.buyLevelsQualityTier, qualityTierRank) * modifier;
    case "refreshedAt":
      return compareDates(a.refreshedAt, b.refreshedAt) * modifier;
    default:
      return compareNumbers(a[sort.key], b[sort.key]) * modifier;
  }
}

export function compareETFRows(a: InvestmentsListRow, b: InvestmentsListRow, sort: DashboardETSort): number {
  const modifier = sort.direction === "asc" ? 1 : -1;
  const aValue = getETFSortValue(a, sort.key);
  const bValue = getETFSortValue(b, sort.key);
  if (typeof aValue === "number" && typeof bValue === "number") {
    return compareNumbers(aValue, bValue) * modifier;
  }
  return compareStrings(aValue, bValue) * modifier;
}

export function buildTriageSummary(
  rows: InvestmentsListRow[],
  configuredTickers: string[],
  now: Date,
): TriageSummary {
  const actionCounts: Record<ValuationZone, number> = {
    BUY: 0,
    HOLD: 0,
    WATCHLIST: 0,
    AVOID: 0,
    UNAVAILABLE: 0,
  };
  for (const row of rows) {
    actionCounts[(row.finalAction || "UNAVAILABLE") as ValuationZone] += 1;
  }
  const topActionable = [...rows]
    .filter((row) => row.finalAction && row.finalAction !== "AVOID" && row.finalAction !== "UNAVAILABLE")
    .sort((a, b) => compareActionableRows(a, b))
    .slice(0, 5);
  const attentionRows = [...rows]
    .sort((a, b) => {
      const redFlags = compareNumbers(b.redFlagCount, a.redFlagCount);
      if (redFlags !== 0) {
        return redFlags;
      }
      const warnings = compareNumbers(b.warningCount, a.warningCount);
      if (warnings !== 0) {
        return warnings;
      }
      const confidence = compareRanks(a.dataConfidence, b.dataConfidence, attentionConfidenceRank);
      if (confidence !== 0) {
        return confidence;
      }
      return compareActionableRows(b, a);
    })
    .slice(0, 5);
  const staleRows = [...rows]
    .sort((a, b) => compareDates(a.refreshedAt, b.refreshedAt))
    .slice(0, 5);
  const cachedTickers = new Set(rows.map((row) => row.ticker));
  const missingTickers = configuredTickers.filter((ticker) => !cachedTickers.has(ticker));

  return {
    actionCounts,
    topActionable,
    attentionRows,
    staleRows,
    missingTickers,
    configuredCount: configuredTickers.length,
    cachedCount: rows.length,
  };
}

export function buildPortfolioSummaries(
  families: InvestmentsListFamily[],
  rows: InvestmentsListRow[],
): PortfolioFamilySummary[] {
  return buildFamilyRows(families, rows).map((section) => {
    const actionCounts: Record<ValuationZone, number> = {
      BUY: 0,
      HOLD: 0,
      WATCHLIST: 0,
      AVOID: 0,
      UNAVAILABLE: 0,
    };
    for (const row of section.rows) {
      actionCounts[(row.finalAction || "UNAVAILABLE") as ValuationZone] += 1;
    }
    const strongest = [...section.rows]
      .sort((a, b) => compareActionableRows(a, b))
      .slice(0, 2);
    const weakest = [...section.rows]
      .sort((a, b) => compareActionableRows(b, a))
      .slice(0, 2);
    return {
      family: section.family,
      rows: section.rows,
      missingTickers: section.missingTickers,
      cachedCount: section.rows.length,
      configuredCount: (section.family.tickers || []).length,
      medianOverall: median(section.rows.map((row) => row.overallScore)),
      medianValuation: median(section.rows.map((row) => row.valuationDisciplineScore)),
      actionCounts,
      totalRedFlags: sum(section.rows.map((row) => row.redFlagCount)),
      totalWarnings: sum(section.rows.map((row) => row.warningCount)),
      strongest,
      weakest,
    };
  });
}

export function buildETFSummary(rows: InvestmentsListRow[], configuredTickers: string[]): ETFSummary {
  const cachedTickers = new Set(rows.map((row) => row.ticker));
  const missingTickers = configuredTickers.filter((ticker) => !cachedTickers.has(ticker));
  const assetClassCounts = new Map<string, number>();
  for (const row of rows) {
    const assetClass = row.etf?.assetClass || "Unknown";
    assetClassCounts.set(assetClass, (assetClassCounts.get(assetClass) || 0) + 1);
  }
  const expenseRatios = rows
    .map((row) => row.etf?.expenseRatio)
    .filter((value): value is number => Number.isFinite(value));
  const highestAum = [...rows]
    .filter((row) => Number.isFinite(row.etf?.assetsUnderManagement))
    .sort((a, b) => compareNumbers(b.etf?.assetsUnderManagement, a.etf?.assetsUnderManagement))
    .slice(0, 3);
  const lowestExpense = [...rows]
    .filter((row) => Number.isFinite(row.etf?.expenseRatio))
    .sort((a, b) => compareNumbers(a.etf?.expenseRatio, b.etf?.expenseRatio))
    .slice(0, 3);

  return {
    missingTickers,
    configuredCount: configuredTickers.length,
    cachedCount: rows.length,
    assetClassMix: Array.from(assetClassCounts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
    medianExpenseRatio: expenseRatios.length > 0 ? median(expenseRatios) : null,
    highestAum,
    lowestExpense,
  };
}

export function paginateRows<T>(rows: T[], page: number, pageSize: number): { rows: T[]; totalPages: number } {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.max(1, Math.min(page, totalPages));
  const start = (safePage - 1) * pageSize;
  return {
    rows: rows.slice(start, start + pageSize),
    totalPages,
  };
}

export function ageInDays(value: string, now: Date): number | null {
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) {
    return null;
  }
  return Math.max(0, Math.floor((now.getTime() - timestamp) / 86_400_000));
}

export function compareActionableRows(a: InvestmentsListRow, b: InvestmentsListRow): number {
  const action = compareRanks(a.finalAction, b.finalAction, actionRank);
  if (action !== 0) {
    return action;
  }
  return compareNumbers(b.overallScore, a.overallScore);
}

function compareDates(a: string | undefined, b: string | undefined): number {
  const aTime = Date.parse(a || "");
  const bTime = Date.parse(b || "");
  const aMissing = Number.isNaN(aTime);
  const bMissing = Number.isNaN(bTime);
  if (aMissing && bMissing) {
    return 0;
  }
  if (aMissing) {
    return 1;
  }
  if (bMissing) {
    return -1;
  }
  return aTime - bTime;
}

function compareNumbers(a: unknown, b: unknown): number {
  const aNumber = typeof a === "number" && Number.isFinite(a) ? a : null;
  const bNumber = typeof b === "number" && Number.isFinite(b) ? b : null;
  if (aNumber === null && bNumber === null) {
    return 0;
  }
  if (aNumber === null) {
    return 1;
  }
  if (bNumber === null) {
    return -1;
  }
  return aNumber - bNumber;
}

function compareStrings(a: unknown, b: unknown): number {
  const aString = typeof a === "string" ? a : "";
  const bString = typeof b === "string" ? b : "";
  return aString.localeCompare(bString);
}

function compareRanks(
  a: string | undefined,
  b: string | undefined,
  rankMap: Record<string, number>,
): number {
  const aRank = a === undefined ? Number.MAX_SAFE_INTEGER : (rankMap[a] ?? Number.MAX_SAFE_INTEGER);
  const bRank = b === undefined ? Number.MAX_SAFE_INTEGER : (rankMap[b] ?? Number.MAX_SAFE_INTEGER);
  return aRank - bRank;
}

function getETFSortValue(row: InvestmentsListRow, key: ETFSortKey): string | number {
  switch (key) {
    case "ticker":
      return row.ticker || "";
    case "companyName":
      return row.companyName || "";
    case "assetClass":
      return row.etf?.assetClass || "";
    case "expenseRatio":
      return row.etf?.expenseRatio ?? Number.NaN;
    case "aum":
      return row.etf?.assetsUnderManagement ?? Number.NaN;
    case "nav":
      return row.etf?.nav ?? Number.NaN;
    case "avgVolume":
      return row.etf?.avgVolume ?? Number.NaN;
    case "holdingsCount":
      return row.etf?.holdingsCount ?? Number.NaN;
    case "domicile":
      return row.etf?.domicile || "";
    default:
      return "";
  }
}

function median(values: Array<number | undefined>): number {
  const finite = values.filter((value): value is number => Number.isFinite(value));
  if (finite.length === 0) {
    return 0;
  }
  const sorted = [...finite].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

function sum(values: Array<number | undefined>): number {
  return values.reduce((total, value) => total + (Number.isFinite(value) ? (value as number) : 0), 0);
}
